import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
  Card,
  ErrorMessage,
  FlexRow,
  Footer,
  PageLoading,
  SaveDialog,
  Spinner,
  SpinnerSize,
  WorkflowHeader,
} from 'ui-toolkit';
import { ReportNavBar } from '../components/ReportNavBar';
import { RequirementsExceptions } from '../components/RequirementsExceptions';
import { SearchReportHeaderButtons } from '../components/SearchReportHeaderButtons/SearchReportHeaderButtons';
import { ReqExcSections } from '../services/RequirementsExceptionsService';
import { RootStore } from '../services/RootStore';
import { IRouterParams } from './IRouterParams';
import { clientUrls } from '../../ui-toolkit/rest/ClientUrlFactory';
import { Copyright } from '../../ui-toolkit/components/Copyright/Copyright';
import { eAccessControlUserType } from '../../ui-toolkit/enums/eAccessControlUserType';
import { upperFirst } from 'lodash';

export interface ISearchReportRequirementsExceptionsProps extends RouteComponentProps<IRouterParams> {
  rootStore: RootStore;
  userType: eAccessControlUserType;
}

interface ISearchReportRequirementsExceptionsState {
  showSaveDialog?: boolean;
  goToNextPage?: () => void;
}

class SearchReportRequirementsExceptions extends React.Component<
  ISearchReportRequirementsExceptionsProps,
  ISearchReportRequirementsExceptionsState
> {
  public readonly state: ISearchReportRequirementsExceptionsState = {};

  constructor(props: ISearchReportRequirementsExceptionsProps) {
    super(props);
    this.props.rootStore.appData!.setTitle(this.props.match.params.action);
    this.props.rootStore.requirementsExceptions!.init(this.props.match.params.orderId);
  }

  public render() {
    const { appData, notifications, requirementsExceptions } = this.props.rootStore;
    if (!appData || !notifications || !requirementsExceptions) return <PageLoading isLoading={true} />;
    const { errorMessage } = appData;
    const action = this.props.match.params.action;
    const searchReportUiUrl = clientUrls.urlFor.searchReport(
      this.props.userType,
      this.props.match.params.orderId,
      action
    );
    const requirementsExceptionsUiUrl = clientUrls.urlFor.searchReportExceptions(
      this.props.userType,
      this.props.match.params.orderId,
      action
    );

    return (
      <div className="App">
        <div>
          <WorkflowHeader
            onBackClick={this.onBackClickHandler}
            onHomeClick={this.onHomeClickHandler}
            title={this.getTitle()}
          >
            <SearchReportHeaderButtons
              showSaveSpinner={notifications.saveState.fetching && !notifications.finalizeState.fetching}
              showSaveSuccess={
                notifications.saveState.success &&
                !errorMessage &&
                !requirementsExceptions.formDataChanged &&
                !notifications.finalizeState.success &&
                !notifications.finalizeState.fetching
              }
              showFinalizeSpinner={notifications.finalizeState.fetching}
              showFinalizeSuccess={!errorMessage && notifications.finalizeState.success}
              disableFinalize={!!errorMessage || !appData.finalizable}
              onSave={this.onSaveHandler}
              onFinalize={this.onFinalizeHandler}
            />
          </WorkflowHeader>

          <ReportNavBar
            options={[
              {
                onClick: (to) => this.safeGoToNextPage(to),
                text: 'Search report',
                to: searchReportUiUrl,
              },
              {
                onClick: (to) => this.safeGoToNextPage(to),
                text: 'Requirements & Exceptions',
                to: requirementsExceptionsUiUrl,
              },
            ]}
          />

          <div className="page-content">
            {errorMessage && <ErrorMessage message={errorMessage} />}

            {this.state.showSaveDialog && (
              <SaveDialog
                errorMessage={errorMessage}
                showSaveSpinner={notifications.saveState.fetching}
                showSaveSuccess={
                  !errorMessage && !requirementsExceptions.formDataChanged && notifications.saveState.success
                }
                onYesClick={this.onYesClickHandler}
                onNoClick={this.state.goToNextPage}
              />
            )}

            {notifications.isFetchingAny ? (
              <FetchingSpinner />
            ) : (
              <>
                <Card className="requirements-exceptions" title={'Schedule B1 - Requirements'}>
                  <RequirementsExceptions
                    addCustomItem={requirementsExceptions.addCustomItem.bind(requirementsExceptions)}
                    section={ReqExcSections.B1}
                    onTextChanged={requirementsExceptions.setChangedText.bind(requirementsExceptions)}
                    addNewItem={requirementsExceptions.addItem.bind(requirementsExceptions)}
                    onDeleteItem={requirementsExceptions.addToDeletionStack.bind(requirementsExceptions)}
                    onOrderChanged={requirementsExceptions.onOrderChanged.bind(requirementsExceptions)}
                    requirementsExceptions={requirementsExceptions.formData[ReqExcSections.B1]}
                    specificExceptions={requirementsExceptions.specificExceptions[ReqExcSections.B1]}
                    fetching={notifications.sendState.fetching}
                  />
                </Card>
                <Card className="requirements-exceptions" title={'Schedule B2 - Exceptions'}>
                  <RequirementsExceptions
                    addCustomItem={requirementsExceptions.addCustomItem.bind(requirementsExceptions)}
                    section={ReqExcSections.B2}
                    onTextChanged={requirementsExceptions.setChangedText.bind(requirementsExceptions)}
                    addNewItem={requirementsExceptions.addItem.bind(requirementsExceptions)}
                    onDeleteItem={requirementsExceptions.addToDeletionStack.bind(requirementsExceptions)}
                    specificExceptions={requirementsExceptions.specificExceptions[ReqExcSections.B2]}
                    onOrderChanged={requirementsExceptions.onOrderChanged.bind(requirementsExceptions)}
                    requirementsExceptions={requirementsExceptions.formData[ReqExcSections.B2]}
                    fetching={notifications.sendState.fetching}
                  />
                </Card>
              </>
            )}
          </div>
        </div>
        <div>
          <Footer title="Save or Submit Search Report & Title Commitment">
            <SearchReportHeaderButtons
              showSaveSpinner={notifications.saveState.fetching && !notifications.finalizeState.fetching}
              showSaveSuccess={
                notifications.saveState.success &&
                !errorMessage &&
                !requirementsExceptions.formDataChanged &&
                !notifications.finalizeState.success &&
                !notifications.finalizeState.fetching
              }
              showFinalizeSpinner={notifications.finalizeState.fetching}
              showFinalizeSuccess={!errorMessage && notifications.finalizeState.success}
              disableFinalize={!!errorMessage || !appData.finalizable}
              onSave={this.onSaveHandler}
              onFinalize={this.onFinalizeHandler}
            />
          </Footer>
          <Copyright />
        </div>
      </div>
    );
  }

  private getTitle = () => {
    const title = upperFirst(this.props.rootStore.appData!.title.replace('new', 'create'));
    return `${title} Search Report & Title Commitment`;
  };

  private safeGoToNextPage = (to: string) => {
    const goToNextPage = () => {
      this.props.history.push(to);
    };

    if (this.props.rootStore.requirementsExceptions!.formDataChanged) {
      this.setState({ goToNextPage, showSaveDialog: true });
    } else {
      this.setState({ goToNextPage: undefined });
      goToNextPage();
    }
  };
  private onSaveHandler = () => this.props.rootStore.requirementsExceptions!.save(this.props.match.params.orderId);
  private onFinalizeHandler = async () => {
    await this.props.rootStore.requirementsExceptions!.finalize(this.props.match.params.orderId);
    const nextPage =
      this.props.userType === eAccessControlUserType.Admin
        ? clientUrls.urlFor.documentsIndex(this.props.match.params.orderId)
        : clientUrls.urlFor.vendorOrder(this.props.match.params.orderId);
    this.safeGoToNextPage(nextPage);
  };

  private onBackClickHandler = () =>
    this.safeGoToNextPage(clientUrls.urlFor.orderPage(this.props.userType, this.props.match.params.orderId));
  private onHomeClickHandler = () =>
    this.safeGoToNextPage(
      this.props.userType === eAccessControlUserType.Admin
        ? clientUrls.urlFor.adminOrders()
        : clientUrls.urlFor.vendorOrders()
    );

  private onYesClickHandler = async () =>
    this.props.rootStore.requirementsExceptions!.save(this.props.match.params.orderId).then(this.state.goToNextPage);
}

export default inject('rootStore')(observer(SearchReportRequirementsExceptions));

const FetchingSpinner = () => (
  <Card>
    <FlexRow>
      <Spinner size={SpinnerSize.regular} />
    </FlexRow>
  </Card>
);
